/* eslint-disable @typescript-eslint/consistent-type-assertions */
import { LOGGER_LOG_TYPE } from 'Config';
import { UserProfile } from 'api/account/models/UserProfile';
import DeviceBrandsService from 'api/deviceBrands/DeviceBrandsService';
import { DeviceBrandDto } from 'api/deviceBrands/models/DeviceBrandDto';
import UsersService from 'api/users/UsersService';
import Loading from 'common/services/Loading';
import Logger from 'common/services/Logger';
import { useEffect, useState } from 'react';
import { Breadcrumb, Button, Col, Form, Row } from 'react-bootstrap';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Reducers } from 'store/types';
import styles from './DeviceBrandScreen.module.scss';
import QuestionYesNo from 'common/components/questionYesNo/QuestionYesNo';
import ScreenTitle from 'common/components/screenTitle/ScreenTitle';

type Props = {
};

const DeviceBrandScreen: React.FC<Props> = () => {
    const { id, type } = useParams<{ id: string, type: string }>();
    const location = useLocation();
    const stateRow = (location.state as { row?: DeviceBrandDto } | null)?.row;
    const [isDetails, setIsDetails] = useState<boolean>(type === 'details');
    const userProfile = useSelector<Reducers, UserProfile | null>(state => state.authentication.profile);
    const hasDeviceBrandsWritePolicy = UsersService.hasPolicies(userProfile?.policies || [], ['CONFIG_DEVICE_BRAND_WRITE']);
    const [showRemoveModal, setShowRemoveModal] = useState<boolean>(false);
    const [itemToRemove, setItemToRemove] = useState<DeviceBrandDto | null>(null);
    const { t } = useTranslation();
    const navigate = useNavigate();
    const [validated, setValidated] = useState(false);
    const [item, setItem] = useState<DeviceBrandDto | undefined>(stateRow);
    const [refresh, setRefresh] = useState<number>(1);
    const [formData, setFormData] = useState<DeviceBrandDto>(stateRow ?? {
        id: '',
        name: '',
        rowVersion: ''
    });

    const getData = async () => {
        try {
            let result: DeviceBrandDto = { id: '', name: '', rowVersion: '' };
            Loading.show();
            if (stateRow) {
                result = stateRow;
            } else if (id) {
                result = await DeviceBrandsService.getById(id);
            }
            setItem(result);
            setFormData(result);
            setRefresh(refresh + 1);
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t get device brand information', error);
            toast.error(t('messages.error_load_info'));
        } finally {
            Loading.hide();
        }
    };

    useEffect(() => {
        getData().catch(console.error);
    }, [id, type]);

    function handleChange(e: any) {
        const key = e.target.name;
        const value = e.target.value;
        setFormData({ ...formData, [key]: value });
    }

    const navigateTo = (typeUrl?: string, id?: string) => {
        if (typeUrl) {
            navigate(`/devicebrand/${typeUrl}/${id}`);
            setIsDetails(typeUrl === 'details');
        } else {
            navigate('/config-devices?tab=brands');
        }
    }

    const onCancelRemove = () => {
        setItemToRemove(null);
        setShowRemoveModal(false);
    };

    const onRemove = async () => {
        if (itemToRemove === null) {
            toast.error(t('messages.record_delete_error'));
            return;
        }
        try {
            await DeviceBrandsService.remove(itemToRemove);
            onCancelRemove();
            navigateTo();
            toast.success(t('messages.record_delete_success'));
        } catch (error) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, 'Couldn\'t delete device brand', error);
            toast.error(t('messages.record_delete_error'));
        }
    };

    const showRemoveItemDialog = async (item: DeviceBrandDto) => {
        setItemToRemove(item);
        setShowRemoveModal(true);
    }

    const onSubmit = async (event: any) => {
        const form = event.currentTarget;
        event.preventDefault();
        event.stopPropagation();
        setValidated(true);
        if (form.checkValidity() === false) {
            return;
        }
        try {
            Loading.show();
            const model: DeviceBrandDto = {
                id: formData.id,
                name: formData.name,
                rowVersion: formData.rowVersion,
            };
            if (model && model.id) {
                await DeviceBrandsService.update(model);
                navigateTo('details', id);
            } else if (model) {
                const newId = await DeviceBrandsService.create(model);
                navigateTo('details', newId);
            }
            Loading.hide();
            toast.success(t('messages.record_save_success'));
        } catch (error: any) {
            Logger.error(LOGGER_LOG_TYPE.REQUEST, `Couldn't update the device brand with id: ${form.id}`, error);
            toast.error(t('messages.error_load_info'));
            Loading.hide();
        }
    };

    return (
        <ScreenTitle title={t('config_devices.brands.title')}>
            <Breadcrumb>
                <Breadcrumb.Item onClick={() => { navigateTo() }}>
                    {t('config_devices.brands.title')}
                </Breadcrumb.Item>
                <Breadcrumb.Item active>{t(`common.${type}`)}</Breadcrumb.Item>
            </Breadcrumb>

            <div className="bg-white rounded-2xl border shadow-sm p-4" style={{ borderColor: '#e2e8f0' }}>
                <Form onSubmit={onSubmit} noValidate validated={validated} key={refresh}>
                    <Row>
                        <Col xs={12}>
                            <Form.Group className="mb-3" controlId="name">
                                <Form.Label>{t('config_devices.brands.list.name')}{!isDetails ? '*' : ''}</Form.Label>
                                <Form.Control required name="name" type="text" defaultValue={item?.name} readOnly={isDetails} onChange={handleChange} placeholder={t('config_devices.brands.list.name')} />
                            </Form.Group>
                        </Col>
                    </Row>
                    <div className={styles.buttonsContainer}>
                        <Button variant="secondary" type="button" className={styles.button} onClick={() => navigateTo()}>
                            {t('common.cancel')}
                        </Button>
                        {isDetails && hasDeviceBrandsWritePolicy &&
                            <Button variant="danger" type="button" className={styles.button} onClick={() => showRemoveItemDialog(item as DeviceBrandDto)} >
                                {t('common.delete')}
                            </Button>
                        }
                        {isDetails && hasDeviceBrandsWritePolicy &&
                            <Button variant="warning" type="button" className={styles.button} onClick={() => navigateTo('edit', item?.id)}>
                                {t('common.edit')}
                            </Button>
                        }
                        {!isDetails && hasDeviceBrandsWritePolicy &&
                            <Button variant="primary" type="submit" className={styles.button}>
                                {t('common.save')}
                            </Button>
                        }
                    </div>
                </Form>
            </div>
            <QuestionYesNo onNo={onCancelRemove} onYes={onRemove} isVisible={showRemoveModal} message={t('messages.remove_record_with_ident', { name: item?.name ?? '' })} />
        </ScreenTitle>
    );
};

export default DeviceBrandScreen;
